import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { getErrorMessage } from "@luce/core/apollo";
import { toast } from "@luce/ui";

import { useClientId } from "../../shared";
import { useClientPaymentMethod } from "../gqlhooks/use-client-payment-method";
import { useCreatePaymentMethod } from "../gqlhooks/use-create-payment-method";
import { useDeletePaymentMethod } from "../gqlhooks/use-delete-payment-method";
import { useTokenizePaymentMethod } from "../gqlhooks/use-tokenize-payment-method";
import type { AddPaymentMethodModalProps } from "./add-payment-method.types";
import {
  cardSchema,
  emptyCardValues,
  parseExpiry,
  sanitizeCardNumber,
  type CardFormValues,
} from "./lib";

type UseAddPaymentMethodArgs = Pick<AddPaymentMethodModalProps, "onSuccess"> & {
  silentSuccess?: boolean;
};

export function useAddPaymentMethod({ onSuccess, silentSuccess }: UseAddPaymentMethodArgs) {
  const clientId = useClientId();
  const form = useForm<CardFormValues>({
    mode: "onChange",
    resolver: zodResolver(cardSchema),
    defaultValues: emptyCardValues,
  });

  // Network-backed: replacing a card deletes the saved one first, so a cold
  // cache must not read back as "no card on file".
  const { paymentMethodId, hasCard } = useClientPaymentMethod({
    fetchPolicy: "cache-and-network",
  });

  const [tokenizePaymentMethod, { loading: tokenizing }] = useTokenizePaymentMethod();
  const [deletePaymentMethod, { loading: deleting }] = useDeletePaymentMethod();
  const [createPaymentMethod, { loading: creating }] = useCreatePaymentMethod();

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const { expMonth, expYear } = parseExpiry(values.expDate);

      const tokenized = await tokenizePaymentMethod({
        variables: {
          input: {
            name: values.name,
            number: sanitizeCardNumber(values.number),
            cvc: values.cvc,
            expMonth,
            expYear,
          },
        },
      });

      const token = tokenized.data?.tokenizePaymentMethod?.token;
      if (!token) throw new Error("Unable to tokenize your card");

      if (paymentMethodId) {
        await deletePaymentMethod({
          variables: { input: { clientId, paymentMethodId } },
        });
      }

      const created = await createPaymentMethod({
        variables: { input: { clientId, token } },
      });
      if (!created.data?.createPaymentMethod?.paymentMethod?.id) {
        throw new Error("Unable to save your card");
      }

      if (!silentSuccess) toast.success("Card added");
      form.reset(emptyCardValues);
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't set up your card. Please try again."));
    }
  });

  return {
    form,
    hasCard,
    isSubmitting: tokenizing || deleting || creating,
    onSubmit,
  };
}
